// front/src/api/Api.js
import axios from "axios";
import TokenService from "./TokenService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8001/api";

const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: "application/json",
  },
});

const REFRESH_URL = "/v1/jwt/refresh/";

// ----------------------------
// Language helper (Accept-Language)
// ----------------------------
function getCurrentLang() {
  // Try common localStorage keys (project may use any of these)
  const candidates = ["lang", "language", "i18n_lang", "i18nLanguage", "locale"];
  for (const k of candidates) {
    try {
      const v = window.localStorage?.getItem?.(k);
      if (v && typeof v === "string") return v.trim();
    } catch (_) {}
  }

  // fallback: html lang
  try {
    const htmlLang = document?.documentElement?.getAttribute?.("lang");
    if (htmlLang) return String(htmlLang).trim();
  } catch (_) {}

  // fallback: navigator
  try {
    const nav = navigator?.language || "en";
    return nav.toLowerCase().startsWith("fa") ? "fa" : "en";
  } catch (_) {}

  return "en";
}

function getAcceptLanguageHeader() {
  const lang = (getCurrentLang() || "en").toLowerCase();
  // send compact values
  return lang.startsWith("fa") ? "fa" : "en";
}

// ----------------------------
// Helpers
// ----------------------------
function getReturnUrl() {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

// ✅ NEW: public routes allowlist (do NOT force redirect to /signin for these)
function isPublicPath(pathname) {
  const path = (pathname || "/").split("?")[0].split("#")[0];

  // Add any truly-public pages here
  const PUBLIC_PATHS = ["/signin", "/support/login", "/signup", "/terms", "/privacy"];

  // match exact or nested, e.g. /terms/...
  return PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

function redirectToSignin() {
  const path = window.location.pathname || "/";

  // ✅ don't redirect if we're already in signin OR current path is public
  if (path.startsWith("/signin")) return;
  if (isPublicPath(path)) return;

  const returnUrl = encodeURIComponent(getReturnUrl());
  const signinPath = path.startsWith("/support/") ? "/support/login" : "/signin";
  window.location.replace(`${signinPath}?returnUrl=${returnUrl}`);
}

function forceLogoutAndRedirect() {
  try {
    TokenService.clear?.();
  } catch (_) {
    TokenService.removeAccessToken?.();
    TokenService.removeUser?.();
    TokenService.removeCustomField?.("refresh_token");
  }
  redirectToSignin();
}

// ----------------------------
// Refresh queue (prevents multiple refresh calls)
// ----------------------------
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, newAccessToken = null) {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${newAccessToken}`;
      resolve(instance(config));
    }
  });
  failedQueue = [];
}

async function refreshAccessToken() {
  const refresh =
    TokenService.getRefreshToken?.() ||
    TokenService.getCustomField?.("refresh_token") ||
    TokenService.getLocalRefreshToken?.();

  if (!refresh) throw new Error("NO_REFRESH_TOKEN");

  const res = await axios.post(
    API_BASE_URL + REFRESH_URL,
    { refresh },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Accept-Language": getAcceptLanguageHeader(), // ✅ NEW
      },
    }
  );

  const newAccess = res?.data?.access;
  const newRefresh = res?.data?.refresh;

  if (!newAccess) throw new Error("REFRESH_NO_ACCESS");

  TokenService.setAccessToken?.(newAccess);
  if (newRefresh) TokenService.setRefreshToken?.(newRefresh);

  return newAccess;
}

// ----------------------------
// Request interceptor
// ----------------------------
instance.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};

    // ✅ NEW: send language to backend
    config.headers["Accept-Language"] = getAcceptLanguageHeader();

    const rawAccess = TokenService.getAccessToken?.() || null;
    const localAccess = TokenService.getLocalAccessToken?.() || null;
    const token = rawAccess || localAccess;

    if (token) config.headers.Authorization = `Bearer ${token}`;
    else delete config.headers.Authorization;

    const isFormData = typeof FormData !== "undefined" && config.data instanceof FormData;

    if (isFormData) {
      delete config.headers["Content-Type"];
    } else if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ----------------------------
// Response interceptor (401 => refresh => retry, else logout)
// ----------------------------
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;
    const url = originalRequest?.url || "";

    if (!originalRequest) return Promise.reject(error);

    const isRefreshRequest = url.includes(REFRESH_URL);
    const pathname = window.location.pathname || "";
    const isSigninRoute = pathname.startsWith("/signin") || pathname.startsWith("/support/login");

    // 👇 فقط 401 را هندل کن، بقیه ارورها (مثل 400 signup) باید برگرده بالا
    if (status !== 401) return Promise.reject(error);

    // ✅ If we're on a public page, DO NOT force redirect on 401.
    // Let the caller handle the error (prevents /terms -> /signin redirects).
    if (isPublicPath(pathname)) {
      return Promise.reject(error);
    }

    if (isRefreshRequest) {
      forceLogoutAndRedirect();
      return Promise.reject(error);
    }

    if (isSigninRoute) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      forceLogoutAndRedirect();
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    isRefreshing = true;

    try {
      const newAccess = await refreshAccessToken();

      processQueue(null, newAccess);
      isRefreshing = false;

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      // keep language header on retry
      originalRequest.headers["Accept-Language"] = getAcceptLanguageHeader();

      return instance(originalRequest);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      isRefreshing = false;

      forceLogoutAndRedirect();
      return Promise.reject(refreshErr);
    }
  }
);

// ----------------------------
// OPTIONAL helpers (Telegram)
// ----------------------------
export const getTelegramStatus = () => instance.get("/v1/telegram/status/");
export const getTelegramLink = () => instance.get("/v1/telegram/link/");

export default instance;
